import { browserLoginScope } from '@/lib/auth/browser-login-scope'
import { createLogger } from '@/lib/logger'
import { desktopAccountSession } from '@/lib/utils/native-credential-store'
import { desktopSecureStorage, isTauri } from '@/lib/utils/tauri'
import { debugLog } from '@/utils/debug'
import { create } from 'zustand'

import type { User } from '@scrambl/shared'
import {
  createSessionScope,
  SessionChangedError,
} from '@scrambl/shared/session-scope'

const logger = createLogger('store/auth.store')
const profileIdentityScope = createSessionScope()

// Define the auth state interface
export interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isAuthReady: boolean
  lastAuthCheckTime: number | null

  // Auth actions
  setAuth: (
    user: User | null,
    isAuthenticated: boolean,
    isLoading: boolean,
  ) => void
  setIsAuthReady: (ready: boolean) => void
  waitForAuthReady: () => Promise<void>
  refreshUser: () => Promise<void>

  // Timezone helper
  getUserTimezone: () => string
}

// Create a store that bridges the React context to Zustand
export const useAuth = create<AuthState>((set, get) => {
  debugLog('[AuthBridge] Initializing auth store')

  // We'll track any ongoing waitForAuthReady promises to avoid creating multiple
  let currentWaitPromise: Promise<void> | null = null
  let isStoreInitialized = false

  return {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isAuthReady: false,
    lastAuthCheckTime: null,

    // Actions
    setAuth: (user, isAuthenticated, isLoading) => {
      const currentState = get()

      // Only update if something has changed to avoid unnecessary renders
      if (
        currentState.user !== user ||
        currentState.isAuthenticated !== isAuthenticated ||
        currentState.isLoading !== isLoading ||
        !isStoreInitialized ||
        currentState.user?.setupComplete !== user?.setupComplete ||
        currentState.user?.timezone !== user?.timezone // Add specific check for timezone changes
      ) {
        debugLog('[AuthBridge] Setting auth state:', {
          userId: user?.id,
          isAuthenticated,
          isLoading,
          isFirstInit: !isStoreInitialized,
          setupComplete: user?.setupComplete,
          timezone: user?.timezone,
        })

        isStoreInitialized = true

        set({
          user,
          isAuthenticated,
          isLoading,
          isAuthReady: !isLoading || currentState.isAuthReady, // Preserve isAuthReady if it's already true
          lastAuthCheckTime: Date.now(),
        })
      }
    },

    // Set auth ready state separately
    setIsAuthReady: ready => {
      // Only update if it's changing to avoid unnecessary renders
      if (get().isAuthReady !== ready) {
        debugLog('[AuthBridge] Setting auth ready state:', ready)
        set({ isAuthReady: ready })
      }
    },

    // Publish only a profile verified for the identity that started this read.
    refreshUser: async () => {
      const identity = captureProfileIdentityScope()
      try {
        // One extra read is allowed only when a legacy vault alias is migrated
        // to the canonical user ID. Never publish the pre-migration response.
        for (let attempt = 0; attempt < 2; attempt++) {
          const credentials = isTauri() ? desktopAccountSession.capture() : null
          if (credentials && !credentials.identity)
            throw new SessionChangedError()
          // Bootstrap is owned by AuthProvider. A refresh must already have
          // a browser profile or an independently selected native account.
          if (!identity.identity && !credentials?.identity)
            throw new SessionChangedError()
          // Load lazily: the account transport observes this store. A static
          // store -> transport import would introduce an initialization cycle.
          const { readCurrentAccountProfile } =
            await import('@/modules/account-profile/api')
          identity.assertCurrent()
          credentials?.assertCurrent()
          const data = await readCurrentAccountProfile()
          identity.assertCurrent()
          credentials?.assertCurrent()
          if (!data.success || !data.data || typeof data.data.id !== 'string') {
            logger.error('[AuthBridge] Failed to refresh user profile')
            return
          }
          if (credentials) {
            await desktopSecureStorage.upsertAccountProfile(
              data.data,
              credentials,
            )
            identity.assertCurrent()
            if (credentials.identity !== data.data.id) {
              if (
                attempt === 0 &&
                desktopAccountSession.capture().identity === data.data.id
              )
                continue
              throw new SessionChangedError()
            }
            credentials.assertCurrent()
          }
          get().setAuth(data.data, true, false)
          return
        }
      } catch (error) {
        if (error instanceof SessionChangedError) return
        logger.warn('[AuthBridge] User profile refresh could not be confirmed')
      }
    },

    // Helper to get the current user timezone with fallback to system timezone
    getUserTimezone: () => {
      return (
        get().user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      )
    },

    // Method to wait for auth to be ready
    waitForAuthReady: async () => {
      // If auth is already ready, return immediately
      if (get().isAuthReady) {
        debugLog('[AuthBridge] Auth already ready, no need to wait')
        return
      }

      // If we already have a promise waiting, reuse it to avoid multiple timers
      if (currentWaitPromise) {
        debugLog('[AuthBridge] Reusing existing wait promise')
        return currentWaitPromise
      }

      debugLog('[AuthBridge] Creating new wait promise for auth to be ready')

      let attempts = 0
      const maxAttempts = 100 // Maximum attempts (5 seconds at 50ms intervals)

      // Create and store the promise
      currentWaitPromise = new Promise<void>((resolve, reject) => {
        const checkAuthReady = () => {
          attempts++

          if (get().isAuthReady) {
            debugLog(
              '[AuthBridge] Auth is now ready after',
              attempts,
              'attempts',
            )
            currentWaitPromise = null // Clear the promise once resolved
            resolve()
            return
          }

          if (attempts >= maxAttempts) {
            logger.error('[AuthBridge] Timeout waiting for auth to be ready')
            currentWaitPromise = null // Clear the promise on failure
            reject(new Error('Timeout waiting for auth to be ready'))
            return
          }

          setTimeout(checkAuthReady, 50)
        }

        checkAuthReady()
      })

      return currentWaitPromise
    },
  }
})

// Observe all updates, including direct setState calls during sign-out or an
// account switch, so returning to the same user cannot revive an old read.
useAuth.subscribe(state =>
  profileIdentityScope.transition(state.user?.id ?? null),
)

/** Tracks profile changes independently of this window's native credential epoch. */
export function captureProfileIdentityScope() {
  const profile = profileIdentityScope.capture()
  const browser = isTauri() ? null : browserLoginScope.capture()
  return {
    ...profile,
    assertCurrent() {
      profile.assertCurrent()
      browser?.assertCurrent()
    },
  }
}
